import { useAtomValue } from "jotai";
import { Spinner } from "@common/index";
import { currencyFormatter } from "@utils/formatter";
import {
    formatSurchargeRate,
    type SurchargeQuoteResponse,
} from "@models/paymentQuote";
import { hasNoLocationMembershipAtom } from "@/store/shipToAtoms";
import { excludedCaveat } from "@customer/_helpers/noLocationCopy";
import { SupportLinkedText } from "@customer/_components/SupportLinkedText";

interface CheckoutSummaryProps {
    /** Total quantity across all lines (not the distinct-line count). */
    itemCount: number;
    subtotal: number;
    /**
     * Lines left out of every figure below because they can't ship to the selected
     * location (#228 / E4-S12). The summary is sticky and sits next to Place Order, so
     * "Order total" must not be read as the amount about to be charged while this is
     * above zero — CreateOrder refuses the whole order with a 412.
     */
    excludedCount: number;
    shippingAndHandling: number;
    orderTotal: number;
    canPlaceOrder: boolean;
    isPlacing: boolean;
    onPlaceOrder: () => void;
    /** Shown under a disabled button to explain the real (non-payment) blocker. */
    disabledReason?: string;
    /**
     * Card-only processing-fee row (#297 / §1.11): true only while Card is the
     * selected method in a payment-configured environment. ACH and unconfigured
     * environments render no row at all — not even a zero.
     */
    showSurcharge?: boolean;
    /**
     * The server's quote for the displayed amount. Server math is authoritative
     * (§1.11): both the rate in the label and the amount render verbatim — this
     * component never computes amount × rate. null = in flight or failed: the
     * amount cell shows an em-dash placeholder until a quote lands (Checkout keeps
     * Place Order disabled meanwhile).
     */
    surchargeQuote?: SurchargeQuoteResponse | null;
    /**
     * The quote request failed and is no longer in flight (#297). Place Order stays
     * disabled — §1.11 forbids an undisclosed fee — but the shopper gets an explicit
     * reason and a Retry instead of a permanent "Calculating…".
     */
    surchargeQuoteFailed?: boolean;
    onRetrySurchargeQuote?: () => void;
}

export const CheckoutSummary = ({
    itemCount,
    subtotal,
    excludedCount,
    shippingAndHandling,
    orderTotal,
    canPlaceOrder,
    isPlacing,
    onPlaceOrder,
    disabledReason,
    showSurcharge,
    surchargeQuote,
    surchargeQuoteFailed,
    onRetrySurchargeQuote,
}: CheckoutSummaryProps) => {
    // #417. Read here, not passed in, so no caller can forget it.
    const hasNoLocationMembership = useAtomValue(hasNoLocationMembershipAtom);

    return (
        <div className="bg-base-100 rounded-lg p-4 md:p-5 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-neutral/70">
                        {itemCount} {itemCount === 1 ? "Item" : "Items"}
                    </span>
                    <span className="text-sm font-semibold text-neutral">
                        {currencyFormatter.format(subtotal)}
                    </span>
                </div>
                {excludedCount > 0 && (
                    <p
                        data-testid="checkout-summary-excluded"
                        className="text-sm text-neutral/70"
                    >
                        {excludedCaveat(excludedCount, hasNoLocationMembership)}
                    </p>
                )}
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-neutral/70">
                        Estimated shipping &amp; handling
                    </span>
                    <span className="text-sm font-semibold text-neutral">
                        {currencyFormatter.format(shippingAndHandling)}
                    </span>
                </div>
                {showSurcharge && (
                    <div className="flex items-baseline justify-between gap-3">
                        <span className="text-sm text-neutral/70">
                            {surchargeQuote
                                ? `Card processing fee (${formatSurchargeRate(
                                    surchargeQuote.rate,
                                )})`
                                : "Card processing fee"}
                        </span>
                        <span className="text-sm font-semibold text-neutral">
                            {surchargeQuote
                                ? currencyFormatter.format(
                                    surchargeQuote.surchargeAmount,
                                )
                                : "—"}
                        </span>
                    </div>
                )}
                {showSurcharge && surchargeQuoteFailed && (
                    // role="alert": the failure arrives asynchronously, after the shopper
                    // has moved on to the rest of the page.
                    <div
                        role="alert"
                        data-testid="surcharge-quote-error"
                        className="mt-1 text-sm text-error"
                    >
                        <p>
                            We couldn&apos;t calculate the card processing fee,
                            so this order can&apos;t be placed yet.
                        </p>
                        <button
                            type="button"
                            onClick={onRetrySurchargeQuote}
                            className="btn btn-ghost btn-xs mt-1 px-0 text-neutral underline"
                        >
                            Try again
                        </button>
                        {/* Names the fee-free escape explicitly: the toggle is a few
                            inches up the page but nothing otherwise connects it to this
                            failure. */}
                        <p className="mt-1 text-xs text-neutral/60">
                            Paying by bank account (ACH) carries no processing
                            fee.
                        </p>
                    </div>
                )}
            </div>

            <div className="flex items-baseline justify-between gap-3 border-t border-base-300 pt-3">
                <span className="text-base font-bold text-neutral">
                    Order total
                </span>
                <span className="text-lg md:text-xl font-bold text-neutral">
                    {currencyFormatter.format(orderTotal)}
                </span>
            </div>

            <button
                type="button"
                onClick={onPlaceOrder}
                disabled={!canPlaceOrder}
                className="btn btn-warning w-full text-neutral font-bold disabled:opacity-60"
            >
                {isPlacing ? <Spinner size="sm" /> : "Place Order"}
            </button>

            {!canPlaceOrder && disabledReason && (
                <p className="text-center text-xs text-neutral/60">
                    {/* The no-location reason names USNara support (#453). */}
                    <SupportLinkedText text={disabledReason} />
                </p>
            )}
        </div>
    );
};
